import { db } from "@/api/db/db.index";
import { insertMessagesSchema } from "@/api/db/schema/message";
import { communityService } from "@/api/services/community.service";
import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import z from "zod";
import { getAllMessages } from "../../api/services/messages/get";
import { createMessage } from "../../api/services/messages/post";
import { getSelectedTag } from "../../api/services/tags/get";
import { authMiddlewareServer } from "../middleware/auth";
import { optionalAuthMiddlewareServer } from "../middleware/auth";

export const getAllMessagesHandler = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      tagId: z.string().min(1, "Tag ID is required"),
    }),
  )
  .middleware([optionalAuthMiddlewareServer])
  .handler(async ({ context, data }) => {
    const question = await getSelectedTag(data.tagId);
    if (!question) throw new Error("Soru bulunamadı");
    if (
      question.moderationStatus !== "published" &&
      context.auth?.userId !== question.createdId &&
      context.auth?.role !== "admin" &&
      context.auth?.role !== "author"
    ) {
      throw new Error("Soru bulunamadı");
    }
    const messages = await getAllMessages(data.tagId);
    return {
      status: 200,
      body: { messages },
    };
  });

export const getAllMessagesOptions = ({ tagId }: { tagId: string }) =>
  queryOptions({
    queryKey: ["messages", tagId],
    queryFn: () => getAllMessagesHandler({ data: { tagId } }),
  });

function calculateTextLength(node: any): number {
  if (!node) return 0;
  let length = 0;

  if (node && typeof node.text === "string") {
    length += node.text.length;
  }
  if (node && node.children && node.children.length > 0) {
    for (const child of node.children) {
      length += calculateTextLength(child);
    }
  }
  if (node && node.content && node.content.length > 0) {
    for (const child of node.content) {
      length += calculateTextLength(child);
    }
  }

  return length;
}

export const createMessageHandler = createServerFn({ method: "POST" })
  .inputValidator(insertMessagesSchema)
  .middleware([authMiddlewareServer])
  .handler(async ({ context, data }) => {
    let messageLength = 0;

    try {
      if (data.messageData) {
        // Try parsing as JSON first
        if (
          data.messageData.trim().startsWith("{") &&
          data.messageData.trim().endsWith("}")
        ) {
          const messageObject = JSON.parse(data.messageData);

          if (messageObject && messageObject.root) {
            messageLength = calculateTextLength(messageObject.root);
          } else if (messageObject && messageObject.type === "doc") {
            messageLength = calculateTextLength(messageObject);
          } else {
            messageLength = data.messageData.replace(/<[^>]*>/g, "").length;
          }
        } else {
          messageLength = data.messageData.replace(/<[^>]*>/g, "").length;
        }
      }
    } catch (_e) {
      // Fallback to HTML tag stripping for text length
      messageLength = (data.messageData || "").replace(/<[^>]*>/g, "").length;
    }

    if (messageLength > 2000) {
      console.error("Exceeded maximum message length", messageLength);
      throw new Error("Message exceeds maximum length");
    }

    const question = await getSelectedTag(data.tagId);
    if (!question) throw new Error("Soru bulunamadı");
    const isExpert =
      context.auth.role === "admin" || context.auth.role === "author";
    if (
      question.moderationStatus !== "published" &&
      !isExpert &&
      question.createdId !== context.auth.userId
    ) {
      throw new Error("Bu soru henüz yanıtlamaya açık değil");
    }
    if (!isExpert) {
      const since = new Date(Date.now() - 24 * 60 * 60 * 1000);
      const recent = await db.query.messages.findMany({
        where: (table, { and, eq, gte }) =>
          and(
            eq(table.senderId, context.auth.userId),
            gte(table.createdAt, since),
          ),
        columns: { id: true },
      });
      if (recent.length >= 30) {
        throw new Error("24 saat içinde en fazla 30 yanıt yazabilirsiniz");
      }
    }
    const message = await createMessage(data, context.auth.userId, isExpert);
    await communityService.setFollowing(
      context.auth.userId,
      "forum_question",
      question.tagId,
      true,
    );
    await communityService.notifyForumReply({
      questionId: question.tagId,
      questionTitle: question.tagTitle,
      questionSlug: question.slug || question.tagId,
      senderId: context.auth.userId,
      isExpert,
    });
    return {
      status: 200,
      body: { success: true, message },
    };
  });
